// ./src/store/slices/designSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from '../index';

export interface DesignFont {
  databaseId: number;
  title: string;
  fontWeight: number;
  fontSource: string;
  fontBaseName: string | null;
  showOnEnvelope?: boolean;
  ttfUrl?: string;
  woff2Url?: string;
  categories?: string[];
}

export interface TextBlock {
  textblockId: string;
  fontFamily: DesignFont;
  fontSize?: number;
  fontColor?: string;
  lineHeight?: number;
  bold?: boolean;
  italic?: boolean;
  text: string;
  width: number;
  x: number;
  y: number;
  alignment?: 'left' | 'center' | 'right';
  zIndex?: number;
}

export interface ImageBlock {
  src: string;
  imageBlockId: string;
  x: number;
  y: number;
  width: number;
  height: string | number;
  zIndex?: number;
}

export interface FigureBlock {
  figureBlockId: string;
  shape: 'square' | 'circle' | 'triangle';
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex?: number;
  fillColor: string;
  strokeStyle: 'solid' | 'dashed' | 'none';
  strokeWidth: number;
  strokeColor: string;
  imageSrc: string | null;
  opacity?: number;
  locked?: boolean;
}

export interface EnvelopeTextFormatting {
  backGreeting: string;
  backShowSenderCompany: boolean;
  backShowSenderName: boolean;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
  fontDesign: {
    databaseId: number;
    fontBaseName: string | null;
    fontSource: string;
    fontWeight: number;
    title: string;
  };
  fontSize: number;
  frontGreeting: string;
  frontShowGuestAddress: boolean;
  frontShowGuestCompany: boolean;
  frontShowGuestName: boolean;
  frontShowRelatives: boolean;
}

export interface EnvelopeState {
  envelopeColor: string;
  envelopeLiningColor: string;
  liningBitmapId?: number | null;
    liningBitmapUrl?: string | null;
  
  // Original design colors for "Fra design" functionality
  originalDesignEnvelopeColor?: string | null;
  originalDesignLiningColor?: string | null;
  originalDesignLiningBitmapId?: number | null;

  textFormatting: EnvelopeTextFormatting;
  originalTextFormatting?: EnvelopeTextFormatting | null;
  viewMode: 'front' | 'back-closed' | 'back-open';
  lastTextView: 'front' | 'back-closed';
  envelopeImageUrl?: string;
  envelopeImageUrlSmall?: string;
  backBottomBehindPartialUrl?: string;
  backBottomInfrontPartialUrl?: string;
  backTopOpenPartialUrl?: string;
  backTopClosedPartialUrl?: string;
  frontPartialUrl?: string;
  includeText?: boolean;
}


export interface DesignBackgroundState {
  designBgUrl: string;
  lpBgColor: string;
  lpTextColor: string;
  backgroundId?: number;
}

export interface CardDesignStatePayload extends Partial<Omit<DesignState, 'envelopeData'>> {
  envelopeData?: Partial<EnvelopeState>;
}

export interface DesignState {
  designId: number | null;
  designTitle: string;
  designTextblocks: TextBlock[];
  removedTextblockIds: string[];
  imageBlocks: ImageBlock[];
  removedImageblockIds: string[];
  figureBlocks: FigureBlock[];
  removedFigureblockIds: string[];
  envelopeData: EnvelopeState;
  backgroundData: DesignBackgroundState;
  cardBackgroundImage: string;
  textBlocksLoaded: boolean;
  activeEditor: string | null;
  error?: string | null;
  designUsageCounts: Record<number, number>;
  searchTerm: string | null;
}

const initialFormatting: EnvelopeTextFormatting = {
  bold: false,
  italic: false,
  underline: false,
  color: '#000000',
  fontSize: 16,
  fontDesign: {
    databaseId: 0,
    fontBaseName: null,
    fontSource: '',
    fontWeight: 400,
    title: '',
  },

  // FRONT
  frontShowGuestName: false,
  frontShowGuestCompany: false,
  frontShowGuestAddress: false,
  frontGreeting: '',

  // BACK
  backGreeting: '',
  backShowSenderCompany: false,
  backShowSenderName: true,
};

// Set initial state
const initialState: DesignState = {
  designId: 0,
  designTitle: '',
  designTextblocks: [],
  cardBackgroundImage: '',
  backgroundData: {
    designBgUrl: '',
    lpBgColor: '',
    lpTextColor: '',
  },
  envelopeData: {
    envelopeColor: '',
    envelopeLiningColor: '',
    liningBitmapId: null,
    liningBitmapUrl: null,

    originalDesignEnvelopeColor: null,
    originalDesignLiningColor: null,
    originalDesignLiningBitmapId: null,
    originalTextFormatting: null,
    
    viewMode: 'back-open',
    lastTextView: 'front',
    textFormatting: {
      bold: false,
      italic: false,
      underline: false,
      color: '#000000',
      fontSize: 16,
      fontDesign: {
        databaseId: 0,
        title: '',
        fontWeight: 400,
        fontSource: '',
        fontBaseName: null,
      },
      frontShowGuestName: false,
      frontShowGuestCompany: false,
      frontShowGuestAddress: false,
      frontShowRelatives: true,  // Show relatives by default
      frontGreeting: '',
      backShowSenderName: true,
      backShowSenderCompany: false,
      backGreeting: '',
    },
  },
  removedTextblockIds: [],
  imageBlocks: [],
  removedImageblockIds: [],
  figureBlocks: [],
  removedFigureblockIds: [],
  activeEditor: null,
  designUsageCounts: {},
  textBlocksLoaded: false,
  error: null,
  searchTerm: null,
};


// Create the design slice
const designSlice = createSlice({
  name: 'design',
  initialState,
  reducers: {
    setCardDesignState(state, action: PayloadAction<CardDesignStatePayload>) {
      state.designTextblocks = action.payload.designTextblocks ?? state.designTextblocks;
      state.textBlocksLoaded = true;
      state.designId = action.payload.designId ?? state.designId;
      state.designTitle = action.payload.designTitle ?? state.designTitle;
      state.backgroundData = action.payload.backgroundData ?? state.backgroundData;

      if (action.payload.envelopeData) {
        state.envelopeData = {
          ...state.envelopeData,
          ...action.payload.envelopeData,
          textFormatting: {
            ...state.envelopeData.textFormatting,
            ...action.payload.envelopeData.textFormatting,
          },
        };
      }
    },

    replaceDesignState(state, action: PayloadAction<DesignState>) {
      return action.payload;
    },

    // 2) Textblock / editor management
    addTextBlock(state, action: PayloadAction<TextBlock>) {
      state.designTextblocks.push(action.payload);
    },
    updateTextBlock(state, action: PayloadAction<TextBlock>) {
      try {
        const updatedBlock = action.payload;
        const index = state.designTextblocks.findIndex((block) => block.textblockId === updatedBlock.textblockId);
        if (index !== -1) {
          state.designTextblocks[index] = { ...updatedBlock };
          state.error = null;
        } else {
          throw new Error('Text block not found for update');
        }
      } catch (error) {
        state.error = (error as Error).message;
        console.error('Failed to update text block:', (error as Error).message);
      }
    },
    deleteTextBlock(state, action: PayloadAction<string>) {
      const textblockId = action.payload;
      state.designTextblocks = state.designTextblocks.filter((block) => block.textblockId !== textblockId);
      state.removedTextblockIds.push(textblockId);
    },
    resetRemovedTextblockIds(state) {
      state.removedTextblockIds = [];
    },
    setCardBackgroundImage(state, action: PayloadAction<string>) {
      state.cardBackgroundImage = action.payload;
    },

    // 3) Envelope partial updates
    setEnvelopeState(state, action: PayloadAction<Partial<EnvelopeState>>) {
      state.envelopeData = {
        ...state.envelopeData, // keep existing envelopeData
        ...action.payload, // overwrite only the fields present
        textFormatting: {
          ...state.envelopeData.textFormatting,
          ...action.payload.textFormatting,
        },
      };
    },
    // 4) Background partial updates
    setDesignBackgroundImageState(state, action: PayloadAction<Partial<DesignBackgroundState>>) {
      state.backgroundData = {
        ...state.backgroundData,
        ...action.payload,
      };
    },

    updateBackgroundField(
      state,
      action: PayloadAction<{
          field: keyof DesignBackgroundState;
          value: string | number;
      }>
    ) {
      if (typeof action.payload.value === 'string' || typeof action.payload.value === 'number') {
        (state.backgroundData[action.payload.field] as string | number) = action.payload.value;
      }
    },

    // 6) Reset
    resetDesignState(state, action: PayloadAction<Partial<DesignState> | undefined>) {
      return { ...initialState, ...action?.payload };
    },
    clearErrorState(state) {
      state.error = null;
    },
    resetTextBlocks(state) {
      state.designTextblocks = [];
      state.textBlocksLoaded = false;
    },
    resetTextBlocksToInitialState(state, action: PayloadAction<TextBlock[]>) {
      state.designTextblocks = action.payload;
      state.textBlocksLoaded = true;
    },

    // 7) Editor state
    setActiveEditor(state, action: PayloadAction<string | null>) {
      state.activeEditor = action.payload;
    },

    // 8) Image block management
    addImageBlock(state, action: PayloadAction<ImageBlock>) {
      const { imageBlockId } = action.payload;
      // Remove from removed list if previously deleted
      state.removedImageblockIds = state.removedImageblockIds.filter((id) => id !== imageBlockId);
      state.imageBlocks.push(action.payload);
    },
    updateImageBlock(state, action: PayloadAction<ImageBlock>) {
      const updatedBlock = action.payload;
      const index = state.imageBlocks.findIndex((block) => block.imageBlockId === updatedBlock.imageBlockId);
      if (index !== -1) {
        state.imageBlocks = [...state.imageBlocks.slice(0, index), { ...updatedBlock }, ...state.imageBlocks.slice(index + 1)];
      } else {
        console.warn('Image block not found for update:', updatedBlock.imageBlockId);
      }
    },
    deleteImageBlock(state, action: PayloadAction<string>) {
      const imageblockId = action.payload;
      state.removedImageblockIds.push(imageblockId);
      state.imageBlocks = state.imageBlocks.filter((block) => block.imageBlockId !== imageblockId);
    },
    moveImageUp(state, action: PayloadAction<string>) {
      const imageBlockId = action.payload;
      const index = state.imageBlocks.findIndex((block) => block.imageBlockId === imageBlockId);
      if (index > 0) {
        // Swap with the previous image (move up in z-order means lower index)
        const newImageBlocks = [...state.imageBlocks];
        [newImageBlocks[index], newImageBlocks[index - 1]] = [newImageBlocks[index - 1], newImageBlocks[index]];
        state.imageBlocks = newImageBlocks;
      }
    },
    moveImageDown(state, action: PayloadAction<string>) {
      const imageBlockId = action.payload;
      const index = state.imageBlocks.findIndex((block) => block.imageBlockId === imageBlockId);
      if (index >= 0 && index < state.imageBlocks.length - 1) {
        // Swap with the next image (move down in z-order means higher index)
        const newImageBlocks = [...state.imageBlocks];
        [newImageBlocks[index], newImageBlocks[index + 1]] = [newImageBlocks[index + 1], newImageBlocks[index]];
        state.imageBlocks = newImageBlocks;
      }
    },
    resetRemovedImageblockIds(state) {
      state.removedImageblockIds = [];
    },

    // 8b) Figure block management
    addFigureBlock(state, action: PayloadAction<FigureBlock>) {
      const { figureBlockId } = action.payload;
      state.removedFigureblockIds = state.removedFigureblockIds.filter((id) => id !== figureBlockId);
      state.figureBlocks.push(action.payload);
    },
    updateFigureBlock(state, action: PayloadAction<{ figureBlockId: string; updates: Partial<FigureBlock> }>) {
      const { figureBlockId, updates } = action.payload;
      const index = state.figureBlocks.findIndex((block) => block.figureBlockId === figureBlockId);
      if (index !== -1) {
        state.figureBlocks[index] = { ...state.figureBlocks[index], ...updates };
      }
    },
    deleteFigureBlock(state, action: PayloadAction<string>) {
      const figureBlockId = action.payload;
      state.removedFigureblockIds.push(figureBlockId);
      state.figureBlocks = state.figureBlocks.filter((block) => block.figureBlockId !== figureBlockId);
    },
    moveFigureBlockUp(state, action: PayloadAction<string>) {
      const figureBlockId = action.payload;
      const index = state.figureBlocks.findIndex((block) => block.figureBlockId === figureBlockId);
      if (index > 0) {
        const newBlocks = [...state.figureBlocks];
        [newBlocks[index], newBlocks[index - 1]] = [newBlocks[index - 1], newBlocks[index]];
        state.figureBlocks = newBlocks;
      }
    },
    moveFigureBlockDown(state, action: PayloadAction<string>) {
      const figureBlockId = action.payload;
      const index = state.figureBlocks.findIndex((block) => block.figureBlockId === figureBlockId);
      if (index >= 0 && index < state.figureBlocks.length - 1) {
        const newBlocks = [...state.figureBlocks];
        [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
        state.figureBlocks = newBlocks;
      }
    },
    resetRemovedFigureblockIds(state) {
      state.removedFigureblockIds = [];
    },

    // 9) Envelope text formatting specifically
    setEnvelopeTextFormatting(state, action: PayloadAction<Partial<EnvelopeTextFormatting>>) {
      state.envelopeData.textFormatting = {
        ...state.envelopeData.textFormatting,
        ...action.payload,
      };
    },
    setEnvelopeViewMode: (state, action: PayloadAction<'front' | 'back-closed' | 'back-open'>) => {
      // sets the actual viewMode
      state.envelopeData.viewMode = action.payload;
      // if user chooses "front" or "back-closed," also store in lastTextView
      if (action.payload === 'front' || action.payload === 'back-closed') {
        state.envelopeData.lastTextView = action.payload;
      }
    },
    setEnvelopePartialUrls: (
        state,
        action: PayloadAction<{
          backBottomBehindPartialUrl: string;
          backBottomInfrontPartialUrl: string;
          backTopOpenPartialUrl: string;
          backTopClosedPartialUrl: string;
        }>
    ) => {
      const {
        backBottomBehindPartialUrl,
        backBottomInfrontPartialUrl,
        backTopOpenPartialUrl,
        backTopClosedPartialUrl
      } = action.payload;
      state.envelopeData.backBottomBehindPartialUrl = backBottomBehindPartialUrl;
      state.envelopeData.backBottomInfrontPartialUrl = backBottomInfrontPartialUrl;
      state.envelopeData.backTopOpenPartialUrl = backTopOpenPartialUrl;
      state.envelopeData.backTopClosedPartialUrl = backTopClosedPartialUrl;
    },
    incrementDesignUsageCount: (state, action: PayloadAction<number>) => {
      const designId = action.payload;
      const prevCount = state.designUsageCounts[designId] ?? 0;
      state.designUsageCounts[designId] = prevCount + 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
    },
  },
});

export const { setCardDesignState, setCardBackgroundImage, setEnvelopeState, setDesignBackgroundImageState, resetDesignState, updateBackgroundField, clearErrorState, resetTextBlocks, resetTextBlocksToInitialState, addTextBlock, updateTextBlock, deleteTextBlock, resetRemovedTextblockIds, setActiveEditor, updateImageBlock, addImageBlock, deleteImageBlock, moveImageUp, moveImageDown, resetRemovedImageblockIds, addFigureBlock, updateFigureBlock, deleteFigureBlock, moveFigureBlockUp, moveFigureBlockDown, resetRemovedFigureblockIds, setEnvelopeTextFormatting, setEnvelopeViewMode, replaceDesignState, setEnvelopePartialUrls, incrementDesignUsageCount, setSearchTerm } = designSlice.actions;

export const selectDesignDataForChanges = createSelector(
  (state: RootState) => state.design,
  (designState) => ({
    designTextblocks: designState.designTextblocks,
    imageBlocks: designState.imageBlocks,
    figureBlocks: designState.figureBlocks || [],
    removedTextblockIds: designState.removedTextblockIds,
    removedImageblockIds: designState.removedImageblockIds,
    removedFigureblockIds: designState.removedFigureblockIds || [],
    envelopeData: designState.envelopeData,
    backgroundData: designState.backgroundData,
    cardBackgroundImage: designState.cardBackgroundImage,
  })
);

// =============================================================================
// 🎯 PHASE 2: Selectors for Original Design Data with Fallback
// =============================================================================
// These selectors read from designSlice (original template) first, 
// falling back to invitationSlice.design.envelopeData.originalDesignXXXX for 
// backward compatibility with old projects.
// =============================================================================

/**
 * Check if designSlice has valid original template data
 * Used to determine if we should use designSlice or legacy fallback
 */
export const selectHasOriginalTemplate = (state: RootState): boolean => 
  !!(state.design.designId && state.design.designTextblocks?.length > 0);

/**
 * Get original envelope color from design template
 * Primary: designSlice.envelopeData.envelopeColor
 * Fallback: invitationSlice.design.envelopeData.originalDesignEnvelopeColor (legacy)
 */
export const selectOriginalEnvelopeColor = (state: RootState): string | null => {
  // Primary source: designSlice (original template)
  if (state.design.designId && state.design.envelopeData?.envelopeColor) {
    return state.design.envelopeData.envelopeColor;
  }
  // Fallback: legacy originalDesignXXXX field in invitationSlice
  return state.invitation.invitationProjectState.design?.envelopeData?.originalDesignEnvelopeColor || null;
};

/**
 * Get original lining color from design template
 * Primary: designSlice.envelopeData.envelopeLiningColor
 * Fallback: invitationSlice.design.envelopeData.originalDesignLiningColor (legacy)
 */
export const selectOriginalLiningColor = (state: RootState): string | null => {
  // Primary source: designSlice (original template)
  if (state.design.designId && state.design.envelopeData?.envelopeLiningColor) {
    return state.design.envelopeData.envelopeLiningColor;
  }
  // Fallback: legacy originalDesignXXXX field in invitationSlice
  return state.invitation.invitationProjectState.design?.envelopeData?.originalDesignLiningColor || null;
};

/**
 * Get original lining bitmap ID from design template
 * Primary: designSlice.envelopeData.liningBitmapId
 * Fallback: invitationSlice.design.envelopeData.originalDesignLiningBitmapId (legacy)
 */
export const selectOriginalLiningBitmapId = (state: RootState): number | null => {
  // Primary source: designSlice (original template)
  // Note: Templates typically don't have bitmap IDs, but check anyway
  if (state.design.designId && state.design.envelopeData?.liningBitmapId != null) {
    return state.design.envelopeData.liningBitmapId;
  }
  // Fallback: legacy originalDesignXXXX field in invitationSlice
  return state.invitation.invitationProjectState.design?.envelopeData?.originalDesignLiningBitmapId ?? null;
};

/**
 * Get original text formatting from design template
 * Primary: designSlice.envelopeData.textFormatting
 * Fallback: invitationSlice.design.envelopeData.originalTextFormatting (legacy)
 */
export const selectOriginalTextFormatting = (state: RootState): EnvelopeTextFormatting | null => {
  // Primary source: designSlice (original template)
  if (state.design.designId && state.design.envelopeData?.textFormatting) {
    return state.design.envelopeData.textFormatting;
  }
  // Fallback: legacy originalDesignXXXX field in invitationSlice
  return state.invitation.invitationProjectState.design?.envelopeData?.originalTextFormatting || null;
};

/**
 * Get original text block color for a specific textblock
 * Primary: designSlice.designTextblocks (original template)
 * No legacy fallback - text block colors were stored in local state (cleanInitialDesignState)
 */
export const selectOriginalTextBlockColor = (state: RootState, textblockId: string): string | null => {
  // Primary source: designSlice (original template)
  const templateBlock = state.design.designTextblocks?.find(tb => tb.textblockId === textblockId);
  if (templateBlock?.fontColor) {
    return templateBlock.fontColor;
  }
  // No legacy fallback for textblock colors - they were stored in component local state
  return null;
};

/**
 * Get a map of all original text block colors from design template
 * Returns Map<textblockId, fontColor>
 */
export const selectOriginalTextBlockColorsMap = createSelector(
  (state: RootState) => state.design.designTextblocks,
  (state: RootState) => state.design.designId,
  (designTextblocks, designId): Map<string, string> | undefined => {
    if (!designId || !designTextblocks?.length) {
      return undefined;
    }
    return new Map(
      designTextblocks
        .filter(block => block.fontColor)
        .map(block => [block.textblockId, block.fontColor!])
    );
  }
);

/**
 * Get original text block font for a specific textblock
 * Primary: designSlice.designTextblocks (original template)
 */
export const selectOriginalTextBlockFont = (state: RootState, textblockId: string): DesignFont | null => {
  // Primary source: designSlice (original template)
  const templateBlock = state.design.designTextblocks?.find(tb => tb.textblockId === textblockId);
  if (templateBlock?.fontFamily) {
    return templateBlock.fontFamily;
  }
  return null;
};

export default designSlice.reducer;
